
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft, Plus, Minus, Star, Clock, MapPin, Sliders, Share2, Gift } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';
import { getRestaurantStatus } from '@/utils/restaurantStatus';
import { ItemCustomizationDialog } from '@/components/ItemCustomizationDialog';
import { MenuItemDetailsDialog } from '@/components/MenuItemDetailsDialog';
import { getKadiKadiSalePrice, getPyfcKitchenSalePrice, getTropicanaSalePrice, getBaaAniDingoSalePrice } from '@/utils/salePricing';
import { useBubblesBogo } from '@/hooks/useBubblesBogo';
import { BUBBLES_TROPICS_ID, BUBBLES_BOGO_CODE } from '@/utils/bubblesBogo';

// Restaurant IDs with sale pricing
const KADI_KADI_ID = '0d29453a-9312-405c-b581-954fe0d2368f';
const PYFC_KITCHEN_ID = 'c625ff68-b35b-4548-9a34-c3a68265b2a0';
const TROPICANA_ID = 'c02c5e24-21ab-494b-a319-7bf46d8d224d';

interface Restaurant {
  id: string;
  name: string;
  description: string;
  image_url: string;
  rating: number;
  address: string;
  is_open: boolean;
  opening_hours: any;
  avg_prep_time_min: number;
  avg_prep_time_max: number;
  business_type: string;
  categories: string[];
}

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  category_id: string;
  is_customizable: boolean;
  original_price?: number;
}

interface ExtendedMenuItem extends MenuItem {
  restaurant_id: string;
  restaurant_name: string;
}

interface Category {
  id: string;
  name: string;
}

const RestaurantPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { items, addItem, updateQuantity, getTotalItems, getRestaurantId, orderType, setOrderType, setCartType, deliveryType } = useCart();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [averageRating, setAverageRating] = useState<number>(0);
  const [customizationDialog, setCustomizationDialog] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState<ExtendedMenuItem | null>(null);
  const [detailsItem, setDetailsItem] = useState<MenuItem | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>({});
  const { visible: bubblesBogoVisible } = useBubblesBogo();
  
  // Refs for scroll detection
  const categoryRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [topBackEl, setTopBackEl] = useState<HTMLButtonElement | null>(null);
  const [topBackVisible, setTopBackVisible] = useState(true);


  const isMarketplace = restaurant?.business_type === 'marketplace';

  const truncateDescription = (description: string) => {
    if (!description) return '';
    
    const sentences = description.match(/[^\.!?]*[\.!?]/g) || [description];
    
    if (sentences.length <= 2) {
      return description;
    }
    
    return sentences.slice(0, 2).join(' ').trim() + '...';
  };

  useEffect(() => {
    setCartType('restaurant'); // Set cart to restaurant mode
  }, [setCartType]);

  // Track when the top header back button scrolls out of view so the sticky
  // category back button only appears when it is actually needed.
  useEffect(() => {
    if (!topBackEl) return;
    const observer = new IntersectionObserver(
      ([entry]) => setTopBackVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(topBackEl);
    return () => observer.disconnect();
  }, [topBackEl]);


  useEffect(() => {
    if (id) {
      fetchRestaurantData();
      fetchRestaurantRating();
      checkRestaurantPromo();
      
      const channel = supabase
        .channel('restaurant-status-changes')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'restaurants',
            filter: `id=eq.${id}`
          },
          (payload) => {
            setRestaurant(payload.new as Restaurant);
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [id]);

  const checkRestaurantPromo = async () => {
    try {
      const { data, error } = await supabase
        .from('promos')
        .select('code')
        .eq('restaurant_id', id)
        .eq('is_active', true)
        .gte('end_date', new Date().toISOString())
        .lte('start_date', new Date().toISOString())
        .maybeSingle();

      if (!error && data && data.code !== BUBBLES_BOGO_CODE) {
        // Store promo code to auto-apply at checkout (skip Bubbles BOGO — handled separately)
        localStorage.setItem('autoApplyPromo', data.code);
      }
    } catch (error) {
      console.error('Error checking restaurant promo:', error);
    }
  };

  const fetchRestaurantData = async () => {
    try {
      const { data: restaurantData, error: restaurantError } = await supabase
        .from('restaurants')
        .select('*')
        .eq('id', id)
        .single();

      if (restaurantError) throw restaurantError;
      
      console.log('Restaurant data fetched:', restaurantData);
      console.log('Prep time min:', restaurantData.avg_prep_time_min);
      console.log('Prep time max:', restaurantData.avg_prep_time_max);
      
      setRestaurant(restaurantData);

      const { data: categoriesData, error: categoriesError } = await supabase
        .from('menu_categories')
        .select('*')
        .eq('restaurant_id', id)
        .order('sort_order');

      if (categoriesError) throw categoriesError;
      setCategories(categoriesData || []);

      const { data: menuItemsData, error: menuItemsError } = await supabase
        .from('menu_items')
        .select('*, is_customizable')
        .eq('restaurant_id', id)
        .eq('is_available', true);

      if (menuItemsError) throw menuItemsError;
      setMenuItems(menuItemsData || []);
      
      // Check if a specific item was requested from explore page
      const itemId = searchParams.get('item');
      if (itemId && menuItemsData) {
        const requestedItem = menuItemsData.find(item => item.id === itemId);
        if (requestedItem && requestedItem.is_customizable && restaurantData) {
          // Automatically show customization dialog for this item
          setSelectedMenuItem({
            ...requestedItem,
            restaurant_id: restaurantData.id,
            restaurant_name: restaurantData.name
          });
          setCustomizationDialog(true);
        }
      }
    } catch (error) {
      console.error('Error fetching restaurant data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurantRating = async () => {
    try {
      const { data: ratingsData, error } = await supabase
        .from('ratings')
        .select('restaurant_rating')
        .eq('restaurant_id', id)
        .not('restaurant_rating', 'is', null);

      if (error) {
        console.error('Error fetching ratings:', error);
        return;
      }

      if (ratingsData && ratingsData.length > 0) {
        const sum = ratingsData.reduce((total, rating) => total + rating.restaurant_rating, 0);
        const avg = sum / ratingsData.length;
        setAverageRating(avg);
      }
    } catch (error) {
      console.error('Error fetching restaurant rating:', error);
    }
  };

  const handleAddToCart = (item: MenuItem) => {
    if (!restaurant) return;

    const statusInfo = getRestaurantStatus(restaurant);
    if (!statusInfo.canOrder) {
      toast.error('Restaurant is currently closed');
      return;
    }

    const currentRestaurantId = getRestaurantId();
    if (currentRestaurantId && currentRestaurantId !== restaurant.id) {
      if (!confirm('Adding this item will clear your current cart. Continue?')) return;
    }

    if (item.is_customizable) {
      setDetailsItem(item);
    } else {
      addItemToCart(item);
    }
  };

  const addItemToCart = (item: any, customizations?: any[], quantity: number = 1, unitPriceOverride?: number) => {
    if (!restaurant) return;

    // Calculate sale price for restaurants with discounts (only for delivery orders)
    let finalPrice = unitPriceOverride ?? Number(item.price);
    if (unitPriceOverride === undefined && orderType === 'delivery') {
      const kadiSale = getKadiKadiSalePrice(restaurant.name, item.price);
      const pyfcSale = getPyfcKitchenSalePrice(restaurant.name, item.price);
      const tropicanaSale = getTropicanaSalePrice(restaurant.name, item.price);
      const baaSale = getBaaAniDingoSalePrice(restaurant.name, item.price);

      if (kadiSale) {
        finalPrice = kadiSale.salePrice;
      } else if (pyfcSale) {
        finalPrice = pyfcSale.salePrice;
      } else if (tropicanaSale) {
        finalPrice = tropicanaSale.salePrice;
      } else if (baaSale) {
        finalPrice = baaSale.salePrice;
      }
    }

    addItem({
      menu_item_id: item.id,
      name: item.name,
      price: finalPrice,
      quantity,
      restaurant_id: restaurant.id,
      restaurant_name: restaurant.name,
      image_url: item.image_url,
      customizations: customizations || [],
      referral_post_id: null,
      cart_type: 'restaurant',
    });
    
  };


  const calculateCustomizedPrice = (basePrice: number, customizations: any[]) => {
    let totalPrice = basePrice;
    
    customizations.forEach((customization: any) => {
      if (customization.selected_options) {
        customization.selected_options.forEach((optionId: string) => {
          // Price calculation will be handled in the customization dialog
          // The final price is already calculated there
        });
      }
    });
    
    return totalPrice;
  };

  const handleUpdateQuantity = (item: MenuItem, newQuantity: number) => {
    if (!restaurant) return;
    
    const statusInfo = getRestaurantStatus(restaurant);
    if (!statusInfo.canOrder) {
      toast.error('Restaurant is currently closed');
      return;
    }
    
    const cartItem = items.find(cartItem => cartItem.menu_item_id === item.id);
    if (cartItem) {
      if (newQuantity <= 0) {
        updateQuantity(cartItem.id, 0);
      } else {
        updateQuantity(cartItem.id, newQuantity);
      }
    }
  };

  const getItemQuantity = (menuItemId: string) => {
    const cartItem = items.find(item => item.menu_item_id === menuItemId);
    return cartItem ? cartItem.quantity : 0;
  };

  // Setup intersection observer for category detection
  useEffect(() => {
    if (categories.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveCategory(entry.target.id);
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: '-100px 0px -50% 0px'
      }
    );

    // Set first category as active initially
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0].id);
    }

    // Observe all category sections
    Object.values(categoryRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    observerRef.current = observer;

    return () => {
      observer.disconnect();
    };
  }, [categories, activeCategory]);

  // Function to scroll to category section
  const scrollToCategory = (categoryId: string) => {
    const element = categoryRefs.current[categoryId];
    if (element) {
      const offset = 120; // Account for sticky header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Group menu items by category
  const groupedItems = categories.reduce((acc, category) => {
    const categoryItems = menuItems.filter(item => item.category_id === category.id);
    if (categoryItems.length > 0) {
      acc[category.id] = {
        category,
        items: categoryItems
      };
    }
    return acc;
  }, {} as { [key: string]: { category: Category; items: MenuItem[] } });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="text-center bg-white rounded-2xl p-8 shadow-lg">
          <p className="text-gray-600 mb-6 text-lg">Restaurant not found</p>
          <Button onClick={() => navigate('/home')} className="bg-red-600 hover:bg-red-700">
            Go Back Home
          </Button>
        </div>
      </div>
    );
  }

  const statusInfo = getRestaurantStatus(restaurant);
  const displayRating = averageRating > 0 ? averageRating : restaurant.rating;
  
  const prepTimeMin = restaurant.avg_prep_time_min || 25;
  const prepTimeMax = restaurant.avg_prep_time_max || 35;
  const prepTimeDisplay = `${prepTimeMin}-${prepTimeMax} min`;

  console.log('Final prep time display:', prepTimeDisplay);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Header with Gradient Overlay */}
      <div className="relative">
        <div className="h-48 sm:h-56 bg-gray-900 overflow-hidden">
          {restaurant.image_url ? (
            <div className="relative h-full">
              <img
                src={restaurant.image_url}
                alt={restaurant.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900 text-gray-400">
              <div className="text-center">
                <div className="text-4xl mb-2">🍽️</div>
                <p className="text-sm">No Image</p>
              </div>
            </div>
          )}
        </div>
        <Button
          ref={setTopBackEl}
          variant="ghost"
          size="icon"
          className="absolute top-4 left-4 bg-white/90 hover:bg-white backdrop-blur-sm shadow-lg"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4 bg-white/90 hover:bg-white backdrop-blur-sm shadow-lg"
          onClick={async () => {
            const shareUrl = `${window.location.origin}/restaurant/${id}`;
            const shareData = {
              title: restaurant?.name || 'Restaurant',
              text: `Check out ${restaurant?.name} on GAMBGO! 🍽️`,
              url: shareUrl,
            };
            if (navigator.share) {
              try { await navigator.share(shareData); } catch {}
            } else {
              await navigator.clipboard.writeText(`${shareData.text}\n${shareUrl}`);
              toast.success('Link copied to clipboard!');
            }
          }}
        >
          <Share2 className="h-5 w-5" />
        </Button>
      </div>

      {/* Restaurant Info Card */}
      <div className="px-4 -mt-6 relative z-10">
        <Card className="bg-white shadow-xl border-0 rounded-2xl overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 pr-4">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{restaurant.name}</h1>
                <p className="text-gray-600 mb-4 leading-relaxed">{restaurant.description}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className={`w-3 h-3 rounded-full ${statusInfo.statusColor} shadow-sm`} />
                <span className={`text-sm font-semibold ${
                  statusInfo.status === 'open' ? 'text-green-600' : 
                  statusInfo.status === 'closing_soon' ? 'text-yellow-600' : 
                  'text-red-600'
                }`}>
                  {statusInfo.statusText}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="p-1 bg-yellow-100 rounded-full">
                  <Star className="h-4 w-4 fill-yellow-500 text-yellow-500" />
                </div>
                <span className="font-semibold text-gray-900">
                  {displayRating > 0 ? displayRating.toFixed(1) : 'No ratings'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Clock className="h-4 w-4" />
                <span>{prepTimeDisplay}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="h-4 w-4" />
                <span className="text-sm">{restaurant.address}</span>
              </div>
            </div>

            <div className="flex gap-3 mb-6">
              <Button
                variant={orderType === 'delivery' ? 'default' : 'outline'}
                onClick={() => setOrderType('delivery')}
                className={`flex-1 py-3 font-semibold transition-all ${
                  orderType === 'delivery' 
                    ? 'bg-red-600 hover:bg-red-700 shadow-lg' 
                    : 'border-2 border-gray-200 hover:border-red-200 hover:bg-red-50'
                }`}
                disabled={!statusInfo.canOrder}
              >
                🚚 Delivery
              </Button>
              <Button
                variant={orderType === 'pickup' ? 'default' : 'outline'}
                onClick={() => setOrderType('pickup')}
                className={`flex-1 py-3 font-semibold transition-all ${
                  orderType === 'pickup' 
                    ? 'bg-red-600 hover:bg-red-700 shadow-lg' 
                    : 'border-2 border-gray-200 hover:border-red-200 hover:bg-red-50'
                }`}
                disabled={!statusInfo.canOrder}
              >
                🏃‍♂️ Pickup
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sticky Categories Navigation */}
      {categories.length > 0 && (
        <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
          <div className="px-4 py-2.5 flex items-center gap-2">
            {!topBackVisible && (
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0 h-9 w-9 rounded-full hover:bg-gray-100"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide flex-1">

              {categories.map((category) => (
                <Button
                  key={category.id}
                  variant={activeCategory === category.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => scrollToCategory(category.id)}
                  className={`whitespace-nowrap px-6 py-2 rounded-full font-medium transition-all ${
                    activeCategory === category.id 
                      ? 'bg-red-600 hover:bg-red-700 shadow-lg text-white' 
                      : 'bg-white border-2 border-gray-200 hover:border-red-200 hover:bg-red-50'
                  }`}
                >
                  {category.name}
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Menu Items by Category */}
      <div className="px-4 pb-32">
        {Object.keys(groupedItems).length > 0 ? (
          <div className="space-y-8">
            {Object.entries(groupedItems).map(([categoryId, { category, items }]) => {
              const isExpanded = expandedCategories[categoryId] || false;
              const displayItems = isMarketplace && !isExpanded ? items.slice(0, 6) : items;
              const hasMore = isMarketplace && items.length > 6;

              return (
                <div
                  key={categoryId}
                  id={categoryId}
                  ref={(el) => (categoryRefs.current[categoryId] = el)}
                  className="scroll-mt-20"
                >
                  <div className="flex items-center justify-between mb-4 sticky top-16 bg-gradient-to-b from-gray-50 to-gray-100 py-2 z-30">
                    <h2 className="text-xl font-bold text-gray-900">
                      {category.name}
                    </h2>
                    {isMarketplace && hasMore && !isExpanded && (
                      <button
                        onClick={() => setExpandedCategories({ ...expandedCategories, [categoryId]: true })}
                        className="text-sm text-red-600 font-semibold hover:text-red-700"
                      >
                        View all ({items.length})
                      </button>
                    )}
                  </div>

                  {isMarketplace ? (
                    <div className="grid grid-cols-3 gap-3">
                      {displayItems.map((item) => {
                        const quantity = getItemQuantity(item.id);
                        
                        return (
                          <Card key={item.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-all">
                            <CardContent className="p-0">
                              <div className="aspect-square relative bg-gray-100">
                                {item.image_url ? (
                                  <img
                                    src={item.image_url}
                                    alt={item.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                                    <div className="text-4xl">📦</div>
                                  </div>
                                )}
                                
                                {quantity === 0 ? (
                                  <button
                                    onClick={() => handleAddToCart(item)}
                                    disabled={!statusInfo.canOrder}
                                    className={`absolute bottom-2 right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg ${
                                      statusInfo.canOrder 
                                        ? 'bg-red-600 hover:bg-red-700 text-white' 
                                        : 'bg-gray-400 cursor-not-allowed text-white'
                                    }`}
                                  >
                                    <Plus className="h-4 w-4" />
                                  </button>
                                ) : (
                                  <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-white rounded-full shadow-lg">
                                    <button
                                      onClick={() => handleUpdateQuantity(item, quantity - 1)}
                                      disabled={!statusInfo.canOrder}
                                      className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded-full"
                                    >
                                      <Minus className="h-3 w-3" />
                                    </button>
                                    <span className="text-xs font-semibold min-w-[16px] text-center">{quantity}</span>
                                    <button
                                      onClick={() => handleUpdateQuantity(item, quantity + 1)}
                                      disabled={!statusInfo.canOrder}
                                      className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded-full"
                                    >
                                      <Plus className="h-3 w-3" />
                                    </button>
                                  </div>
                                )}
                              </div>
                              
                              <div className="p-2">
                                <div className="flex flex-col mb-1">
                                  {/* Sale pricing - only for delivery */}
                                  {(() => {
                                    if (orderType !== 'delivery') {
                                      // Pickup - show original price
                                      return (
                                        <p className="font-semibold text-sm text-gray-900">
                                          D{item.price}
                                        </p>
                                      );
                                    }
                                    // Delivery mode - show sales
                                    const kadiSale = restaurant && getKadiKadiSalePrice(restaurant.name, item.price);
                                    const pyfcSale = restaurant && getPyfcKitchenSalePrice(restaurant.name, item.price);
                                    const tropicanaSale = restaurant && getTropicanaSalePrice(restaurant.name, item.price);
                                    const baaSale = restaurant && getBaaAniDingoSalePrice(restaurant.name, item.price);
                                    
                                    // Kadi Kadi, PYFC, Tropicana or Baa Ani Dingo sale
                                    const saleInfo = kadiSale || pyfcSale || tropicanaSale || baaSale;
                                    if (saleInfo) {
                                      return (
                                        <>
                                          <p className="text-xs text-red-400 line-through leading-tight">D{saleInfo.originalPrice}</p>
                                          <p className="font-semibold text-sm text-gray-900">D{saleInfo.salePrice}</p>
                                        </>
                                      );
                                    }
                                    // Regular pricing
                                    return <p className="font-semibold text-sm text-gray-900">D{item.price}</p>;
                                   })()}
                                 </div>
                                 <p className="text-xs text-gray-600 line-clamp-2">{item.name}</p>
                                 {bubblesBogoVisible && restaurant?.id === BUBBLES_TROPICS_ID && (
                                   <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-pink-500 px-2 py-1 text-[10px] font-bold text-white shadow-sm">
                                     <Gift className="h-3 w-3" />
                                     BUY 1 GET 1
                                   </div>
                                 )}
                               </div>
                             </CardContent>
                           </Card>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {items.map((item) => {
                        const quantity = getItemQuantity(item.id);
                        
                        return (
                          <Card
                            key={item.id}
                            className="bg-white border-0 shadow-lg rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
                            onClick={() => setDetailsItem(item)}
                          >
                            <CardContent className="p-0">
                              <div className="flex">
                                 <div className="flex-1 p-5">
                                    <div className="mb-2">
                                      <h3 className="font-bold text-lg text-gray-900 leading-tight">{item.name}</h3>
                                      {bubblesBogoVisible && restaurant?.id === BUBBLES_TROPICS_ID && (
                                        <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-pink-100 px-2 py-1 text-[10px] font-bold text-pink-700">
                                          <Gift className="h-3 w-3" />
                                          BUY 1 GET 1
                                        </div>
                                      )}
                                    </div>
                                   <p className="text-sm text-gray-600 mb-3 leading-relaxed line-clamp-2">
                                    {item.description}
                                  </p>
                                    <div className="flex items-center justify-between">
                                     <div className="flex flex-col">
                                       <div className="flex flex-col">
                                         {/* Sale pricing - only for delivery */}
                                         {(() => {
                                           if (orderType !== 'delivery') {
                                             // Pickup - show original price
                                             return (
                                               <p className="font-bold text-xl text-red-600">
                                                 D{item.price}
                                               </p>
                                             );
                                           }
                                           // Delivery mode - show sales
                                           const kadiSale = restaurant && getKadiKadiSalePrice(restaurant.name, item.price);
                                           const pyfcSale = restaurant && getPyfcKitchenSalePrice(restaurant.name, item.price);
                                           const tropicanaSale = restaurant && getTropicanaSalePrice(restaurant.name, item.price);
                                           const baaSale = restaurant && getBaaAniDingoSalePrice(restaurant.name, item.price);
                                           
                                           // Any sale
                                           const saleInfo = kadiSale || pyfcSale || tropicanaSale || baaSale;
                                           if (saleInfo) {
                                             return (
                                               <>
                                                 <p className="text-base text-red-400 line-through leading-tight">D{saleInfo.originalPrice}</p>
                                                 <p className="font-bold text-xl text-red-600">D{saleInfo.salePrice}</p>
                                               </>
                                             );
                                           }
                                           // Regular pricing
                                           return <p className="font-bold text-xl text-red-600">D{item.price}</p>;
                                         })()}
                                       </div>
                                      {item.is_customizable && (
                                        <Sliders className="h-4 w-4 text-gray-500 mt-1" />
                                      )}
                                    </div>
                                    
                                    {quantity === 0 ? (
                                      <Button
                                        size="sm"
                                        className={`px-4 py-2 rounded-full font-semibold transition-all ${
                                          statusInfo.canOrder 
                                            ? 'bg-red-600 hover:bg-red-700 shadow-lg hover:shadow-xl' 
                                            : 'bg-gray-400 cursor-not-allowed'
                                        }`}
                                        onClick={(e) => { e.stopPropagation(); handleAddToCart(item); }}
                                        disabled={!statusInfo.canOrder}
                                      >
                                        <Plus className="h-4 w-4 mr-1" />
                                        Add
                                      </Button>
                                    ) : (
                                      <div className="flex items-center gap-3 bg-gray-50 rounded-full p-1" onClick={(e) => e.stopPropagation()}>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 rounded-full hover:bg-red-100"
                                          onClick={(e) => { e.stopPropagation(); handleUpdateQuantity(item, quantity - 1); }}
                                          disabled={!statusInfo.canOrder}
                                        >
                                          <Minus className="h-4 w-4" />
                                        </Button>
                                        <span className="w-8 text-center font-semibold text-gray-900">{quantity}</span>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 rounded-full hover:bg-red-100"
                                          onClick={(e) => { e.stopPropagation(); handleUpdateQuantity(item, quantity + 1); }}
                                          disabled={!statusInfo.canOrder}
                                        >
                                          <Plus className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                {item.image_url && (
                                  <div className="w-28 h-28 sm:w-32 sm:h-32 flex-shrink-0 m-4">
                                    <div className="w-full h-full bg-gray-200 rounded-2xl overflow-hidden">
                                      <img
                                        src={item.image_url}
                                        alt={item.name}
                                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="bg-white rounded-2xl p-8 shadow-lg max-w-md mx-auto">
              <div className="text-6xl mb-4">🍽️</div>
              <p className="text-gray-600 text-lg mb-2">No menu items available</p>
              <p className="text-sm text-gray-500">at the moment.</p>
            </div>
          </div>
        )}
      </div>

      <ItemCustomizationDialog
        isOpen={customizationDialog}
        onClose={() => setCustomizationDialog(false)}
        menuItem={selectedMenuItem}
        onAddToCart={addItemToCart}
      />

      <MenuItemDetailsDialog
        isOpen={!!detailsItem}
        onClose={() => setDetailsItem(null)}
        item={detailsItem}
        displayPrice={(() => {
          if (!detailsItem || !restaurant || orderType !== 'delivery') return detailsItem?.price;
          const sale =
            getKadiKadiSalePrice(restaurant.name, detailsItem.price) ||
            getPyfcKitchenSalePrice(restaurant.name, detailsItem.price) ||
            getTropicanaSalePrice(restaurant.name, detailsItem.price) ||
            getBaaAniDingoSalePrice(restaurant.name, detailsItem.price);
          return sale ? sale.salePrice : detailsItem.price;
        })()}
        originalPrice={(() => {
          if (!detailsItem || !restaurant || orderType !== 'delivery') return undefined;
          const sale =
            getKadiKadiSalePrice(restaurant.name, detailsItem.price) ||
            getPyfcKitchenSalePrice(restaurant.name, detailsItem.price) ||
            getTropicanaSalePrice(restaurant.name, detailsItem.price) ||
            getBaaAniDingoSalePrice(restaurant.name, detailsItem.price);
          return sale ? sale.originalPrice : undefined;
        })()}
        canOrder={statusInfo.canOrder}
        onAddToCart={(it, qty, customizations, finalUnitPrice) => {
          addItemToCart(it, customizations, qty, finalUnitPrice);
        }}
      />



      {getTotalItems() > 0 && (
        <div className="fixed bottom-6 left-4 right-4 z-50">
          <Button 
            className={`w-full py-4 text-white font-bold text-lg rounded-2xl shadow-2xl transition-all ${
              statusInfo.canOrder 
                ? 'bg-red-600 hover:bg-red-700 hover:shadow-3xl' 
                : 'bg-gray-400 cursor-not-allowed'
            }`}
            onClick={() => statusInfo.canOrder && navigate('/cart')}
            disabled={!statusInfo.canOrder}
          >
            <div className="flex items-center justify-center gap-3">
              <div className="bg-white/20 rounded-full px-3 py-1">
                {getTotalItems()}
              </div>
              <span>View Cart</span>
              <div className="bg-white/20 rounded-full px-3 py-1">
                D{items.reduce((total, item) => total + (item.price * item.quantity), 0).toFixed(2)}
              </div>
            </div>
          </Button>
        </div>
      )}
    </div>
  );
};

export default RestaurantPage;
